import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { Upload, ImagePlus, CheckCircle2, ArrowLeft, Clock, XCircle, RefreshCw, Crop } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { notifyPhotoSubmission } from "@/lib/notifications.functions";
import {
  ASPECTS,
  formatBytes,
  processImage,
  type AspectKey,
  type ProcessedImage,
} from "@/lib/image-processing";

const STORAGE_KEY = "ccl.gallery.submissions.v1";
type StoredSubmission = { id: string; submittedAt: string; caption?: string | null };
type StatusRow = { id: string; status: "pending" | "approved" | "rejected"; caption: string | null; created_at: string };

function loadStored(): StoredSubmission[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveStored(items: StoredSubmission[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export const Route = createFileRoute("/submit-photo")({
  head: () =>
    seoHead({
      path: "/submit-photo",
      title: "Submit a Photo — Coffman Cove Lodging",
      description:
        "Share your Coffman Cove Lodging catch and trip photos. Submissions are reviewed by the Hedges family before being added to the gallery.",
    }),
  component: SubmitPhotoPage,
});

const MAX_MB = 15;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/heic"];

const schema = z.object({
  name: z.string().trim().min(1, "Please tell us your name").max(80),
  email: z
    .string()
    .trim()
    .max(200)
    .email("Enter a valid email")
    .optional()
    .or(z.literal("")),
  caption: z.string().trim().max(500).optional().or(z.literal("")),
});

function SubmitPhotoPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [aspect, setAspect] = useState<AspectKey>("original");
  const [offsetX, setOffsetX] = useState(0.5);
  const [offsetY, setOffsetY] = useState(0.5);
  const [quality, setQuality] = useState(0.82);
  const [processed, setProcessed] = useState<ProcessedImage | null>(null);
  const [processing, setProcessing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [caption, setCaption] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [stored, setStored] = useState<StoredSubmission[]>([]);
  const [statuses, setStatuses] = useState<Record<string, StatusRow>>({});
  const [refreshing, setRefreshing] = useState(false);

  const refreshStatuses = useCallback(async (items: StoredSubmission[]) => {
    if (items.length === 0) {
      setStatuses({});
      return;
    }
    setRefreshing(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc("get_submissions_status", {
        _ids: items.map((s) => s.id),
      });
      if (rpcErr) throw rpcErr;
      const map: Record<string, StatusRow> = {};
      for (const row of (data ?? []) as StatusRow[]) map[row.id] = row;
      setStatuses(map);
    } catch {
      // silent — stale statuses just remain
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const items = loadStored();
    setStored(items);
    void refreshStatuses(items);
  }, [refreshStatuses, done]);

  function onPick(f: File | null) {
    setError(null);
    setProcessed(null);
    setAspect("original");
    setOffsetX(0.5);
    setOffsetY(0.5);
    if (!f) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (!ACCEPTED.includes(f.type)) {
      setError("Please upload a JPG, PNG, WEBP, or HEIC image.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`Images must be under ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  // Re-run crop/compression whenever the file or its settings change.
  useEffect(() => {
    if (!file) {
      setProcessed(null);
      return;
    }
    let cancelled = false;
    let created: string | null = null;
    setProcessing(true);
    processImage(file, {
      ratio: ASPECTS.find((a) => a.key === aspect)?.ratio ?? null,
      offsetX,
      offsetY,
      maxEdge: 2000,
      quality,
    })
      .then((result) => {
        if (cancelled) {
          URL.revokeObjectURL(result.url);
          return;
        }
        created = result.url;
        setProcessed(result);
      })
      .catch(() => {
        // e.g. HEIC that the browser can't decode — fall back to the raw file.
        if (!cancelled) setProcessed(null);
      })
      .finally(() => {
        if (!cancelled) setProcessing(false);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [file, aspect, offsetX, offsetY, quality]);


  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError("Please choose a photo to upload.");
      return;
    }
    const parsed = schema.safeParse({ name, email, caption });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please check your entries.");
      return;
    }

    setSubmitting(true);
    try {
      const body: Blob = processed?.blob ?? file;
      const contentType = processed ? "image/jpeg" : file.type;
      const ext = processed ? "jpg" : (file.name.split(".").pop()?.toLowerCase() ?? "jpg");
      const submissionId = crypto.randomUUID();
      const path = `pending/${submissionId}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from("gallery-submissions")
        .upload(path, body, { contentType, upsert: false });
      if (uploadErr) throw uploadErr;

      const { error: insertErr } = await supabase
        .from("gallery_submissions")
        .insert({
          id: submissionId,
          submitter_name: parsed.data.name,
          submitter_email: parsed.data.email || null,
          caption: parsed.data.caption || null,
          storage_path: path,
          status: "pending",
        });
      if (insertErr) throw insertErr;

      const next: StoredSubmission[] = [
        { id: submissionId, submittedAt: new Date().toISOString(), caption: parsed.data.caption || null },
        ...loadStored(),
      ].slice(0, 25);
      saveStored(next);
      setStored(next);
      await refreshStatuses(next);

      setDone(true);
      void notifyPhotoSubmission({
        data: {
          submissionId,
          submitterName: parsed.data.name,
          submitterEmail: parsed.data.email || null,
          caption: parsed.data.caption || null,
        },
      }).catch(() => {});

    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-20 text-center">
        <CheckCircle2
          size={56}
          className="mx-auto text-[var(--color-ember)]"
          aria-hidden
        />
        <h1 className="font-display text-3xl md:text-4xl mt-6">
          Thanks for sharing your catch!
        </h1>
        <p className="mt-4 text-muted-foreground">
          Your photo has been submitted and is pending review. If it's a great fit,
          you'll see it in the gallery soon.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => {
              setDone(false);
              setFile(null);
              setPreview(null);
              setName("");
              setEmail("");
              setCaption("");
            }}
            className="rounded-md border border-border px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-muted"
          >
            Submit another
          </button>
          <button
            onClick={() => navigate({ to: "/reviews" })}
            className="rounded-md bg-[var(--color-ember)] px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
          >
            Back to Reviews
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-[var(--color-deep)] text-[var(--color-parchment)]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-14 sm:py-16">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/80 hover:text-white"
          >
            <ArrowLeft size={14} /> Back to Reviews
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl mt-4 text-white text-balance">
            Submit a photo to the gallery
          </h1>
          <p className="mt-4 text-white/85 max-w-2xl">
            Send us your favorite shot from your Coffman Cove trip — catches, sunsets,
            eagles, whatever tells the story. All submissions are reviewed by the Hedges
            family before appearing in the public gallery.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-14">
        <form
          onSubmit={onSubmit}
          className="rounded-lg border border-border bg-card p-6 sm:p-8 space-y-6"
        >
          {/* File */}
          <div>
            <label className="text-sm font-semibold">Photo *</label>
            <label
              className="mt-2 flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border p-8 text-center cursor-pointer hover:bg-muted/50 transition"
            >
              {preview ? (
                <img
                  src={processed?.url ?? preview}
                  alt="Preview of the photo you're submitting"
                  className="max-h-64 rounded-md object-contain"
                />
              ) : (
                <>
                  <ImagePlus size={36} className="text-muted-foreground" aria-hidden />
                  <div>
                    <p className="text-sm font-medium">Click to choose a photo</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPG, PNG, WEBP or HEIC — up to {MAX_MB} MB
                    </p>
                  </div>
                </>
              )}
              <input
                type="file"
                accept={ACCEPTED.join(",")}
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0] ?? null)}
              />
            </label>
            {file && (
              <p className="mt-2 text-xs text-muted-foreground truncate">
                {file.name} — {formatBytes(file.size)}
                {processed && (
                  <>
                    {" → "}
                    <span className="font-medium text-foreground">
                      {processed.width}×{processed.height}, {formatBytes(processed.bytes)}
                    </span>
                  </>
                )}
                {processing && " · processing…"}
              </p>
            )}

            {file && (
              <div className="mt-4 rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Crop size={15} aria-hidden /> Crop &amp; compress
                </div>

                <div>
                  <span className="text-xs font-medium text-muted-foreground">Shape</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {ASPECTS.map((a) => (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => setAspect(a.key)}
                        aria-pressed={aspect === a.key}
                        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                          aspect === a.key
                            ? "border-[var(--color-ember)] bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
                            : "border-border hover:bg-muted"
                        }`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>

                {aspect !== "original" && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Horizontal position
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={offsetX}
                        onChange={(e) => setOffsetX(Number(e.target.value))}
                        className="mt-2 w-full accent-[var(--color-ember)]"
                      />
                    </label>
                    <label className="block text-xs font-medium text-muted-foreground">
                      Vertical position
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={offsetY}
                        onChange={(e) => setOffsetY(Number(e.target.value))}
                        className="mt-2 w-full accent-[var(--color-ember)]"
                      />
                    </label>
                  </div>
                )}

                <label className="block text-xs font-medium text-muted-foreground">
                  Quality — {Math.round(quality * 100)}%
                  <input
                    type="range"
                    min={0.4}
                    max={0.95}
                    step={0.01}
                    value={quality}
                    onChange={(e) => setQuality(Number(e.target.value))}
                    className="mt-2 w-full accent-[var(--color-ember)]"
                  />
                </label>

                <p className="text-xs text-muted-foreground">
                  Photos are resized to a 2000px longest edge and saved as JPEG so they
                  upload fast. The preview above shows exactly what gets submitted.
                </p>
              </div>
            )}
          </div>


          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="name" className="text-sm font-semibold">
                Your name *
              </label>
              <input
                id="name"
                type="text"
                required
                maxLength={80}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Jane Angler"
              />
            </div>
            <div>
              <label htmlFor="email" className="text-sm font-semibold">
                Email (optional)
              </label>
              <input
                id="email"
                type="email"
                maxLength={200}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="caption" className="text-sm font-semibold">
              Caption (optional)
            </label>
            <textarea
              id="caption"
              rows={3}
              maxLength={500}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-y"
              placeholder="Tell us about the shot — species, date, story..."
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {caption.length}/500
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-600"
            >
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-muted-foreground max-w-sm">
              By submitting, you confirm you took the photo and give Coffman Cove Lodging
              permission to feature it on the site.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90 disabled:opacity-60"
            >
              <Upload size={16} />
              {submitting ? "Submitting…" : "Submit Photo"}
            </button>
          </div>
        </form>

        <YourSubmissions
          stored={stored}
          statuses={statuses}
          refreshing={refreshing}
          onRefresh={() => refreshStatuses(stored)}
        />
      </section>
    </div>
  );
}

function YourSubmissions({
  stored,
  statuses,
  refreshing,
  onRefresh,
}: {
  stored: StoredSubmission[];
  statuses: Record<string, StatusRow>;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  if (stored.length === 0) return null;

  const badge = (status: StatusRow["status"] | undefined) => {
    if (status === "approved") {
      return {
        label: "Approved",
        icon: CheckCircle2,
        className: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
      };
    }
    if (status === "rejected") {
      return {
        label: "Not selected",
        icon: XCircle,
        className: "bg-red-500/10 text-red-700 border-red-500/30",
      };
    }
    return {
      label: "Pending review",
      icon: Clock,
      className: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    };
  };

  return (
    <div className="mt-10 rounded-lg border border-border bg-card p-6 sm:p-8">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl">Your submissions</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Tracked on this device. Approved photos appear in the public gallery.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-60"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <ul className="mt-4 divide-y divide-border">
        {stored.map((item) => {
          const row = statuses[item.id];
          const b = badge(row?.status);
          const Icon = b.icon;
          const submitted = new Date(item.submittedAt);
          return (
            <li key={item.id} className="py-3 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {row?.caption ?? item.caption ?? "Untitled photo"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Submitted {submitted.toLocaleDateString()} · ID {item.id.slice(0, 8)}
                </p>
              </div>
              <span
                className={`inline-flex flex-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${b.className}`}
              >
                <Icon size={12} />
                {b.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
